import { useContext, createContext } from "react";
import { useNavigate } from "react-router-dom";

import { Id } from "../types";
import { ROUTES } from "../constants/routes";

import useLocalStorage from "../hooks/UseLocalStorage";

type User = {
  id?: Id;
  email: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string | null;
  bio?: string | null;
  subjects?: string[];
  countryCode?: string | null;
  countryName?: string | null;
  avatarUrlLarge?: string | null;
  avatarUrlSmall?: string | null;
  isOnboarded?: boolean;
};

type UserContextValues = User & {
  appInstalledViewed: boolean;
  token: string | null;
  setUser(userData: User, token: string | null): void;
  updateProfile(updates: Partial<User>): void;
  setAppInstalledViewed(isInstalled: boolean): void;
  logOut(): void;
};

const defaultValues: UserContextValues = {
  id: undefined,
  firstName: "",
  email: "",
  token: null,
  appInstalledViewed: false,
  setUser: () => {},
  updateProfile: () => {},
  setAppInstalledViewed: () => {},
  logOut: () => {},
};

export const UserContext = createContext<UserContextValues>(defaultValues);

export function useUserContext() {
  return useContext(UserContext);
}

type UserProviderProps = {
  children: React.ReactNode;
};

export function UserProvider({ children }: UserProviderProps): JSX.Element {
  const navigate = useNavigate();
  const { value: appInstalledViewed, setValue: setAppInstalledViewed } =
    useLocalStorage("pwa-install", false);
  const { value: userToken, setValue: setUserToken } = useLocalStorage(
    "jwt-attendance",
    null
  );
  const defaultUser: User = { id: undefined, firstName: "", email: "" };
  const { value: user, setValue: setUser } = useLocalStorage<User>(
    "attendance-user",
    defaultUser
  );

  function updateUSer(userData: User, token: string | null) {
    setUser(userData);
    setUserToken(token);
  }

  function updateProfile(updates: Partial<User>) {
    setUser({ ...user, ...updates });
  }

  function logOut() {
    //* Clear jwt from local storage
    setUserToken(null);
    //* Clear user from local storage
    setUser(defaultUser);
    //* navigate to HOME page without the possibility to do "back" from the browser
    navigate(ROUTES.LOG_IN, { replace: true });
  }

  const value = {
    ...user,
    appInstalledViewed: appInstalledViewed,
    token: userToken,
    setUser: updateUSer,
    updateProfile,
    setAppInstalledViewed,
    logOut,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
